import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { LtbDataService } from '../ltb.data.service';
import { UsageType } from '../models/usageEventType';
import { LateFunding } from '../models/lateFundingEvent';
import { LateUtilityFeeAdjustment } from '../models/lufaEvent';
import { OpenClose } from '../models/OpendCloseEvent';
import { TitleService } from '../../shared/services/title.service';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { Subscription} from 'rxjs';
import { AutoUnsubscribe } from '../../shared/decorators/AutoUnsubscribe';
import { SharedDataService } from '../../shared/services/shared-data.service';
import { ClientBillingAgreement, ManualUsageEvent } from 'src/app/shared/api/models';
import { LTBManualFormModel } from 'src/app/shared/classes/models/LTBManualFormModel';
import { ManualFormMapper } from 'src/app/shared/classes/models/LTBManualFormMapper';
import { ManualUsageEventService, ClientBillingAgreementService } from 'src/app/shared/api/services';
import { matchClientPartyRow } from '../../shared/classes/helpers';

  // -------- Custom Validation ---------------
  function nonZeroValidator(control: AbstractControl): {[key: string]: boolean} | null {
    if(( Number(control.value) === 0 || isNaN(control.value)) && control.value !== null)
    {
      return {'nonZero': true};
    }
    return null;
  }

  function isNumberValidator(control: AbstractControl): {[key: string]: boolean} | null {
    if(isNaN(control.value) || control.value === null)
    {
      return {'nonNumber': true};
    }
    return null;
  }

@Component({
  selector: 'ltb-manual-form',
  templateUrl: './ltb-manual-form.component.html',
  styleUrls: ['./ltb-manual-form.component.scss']
})

@AutoUnsubscribe
export class LtbManualFormComponent implements OnInit {
  @ViewChild('template_file_upload') templateFileUpload;

  @Input() set existingManualEvent(data: ManualUsageEvent) {
    this._existingManualEvent = data;
    this.intializeForm();
    if(data) this.manualUsageEventForm = ManualFormMapper.MapFromManualUsageEvent(this._existingManualEvent, this.manualUsageEventForm);
  }
  get existingManualEvent() {
    return this._existingManualEvent;
  }

  // #region public
  matchClientPartyRow = matchClientPartyRow;
  isShown: boolean;
  submitClicked: boolean;
  usageEventTypes = UsageType;
  public files: Set<File> = new Set();
  showSpinner: boolean;
  manualUsageEventForm: FormGroup;
  now: Date;
  currentBillingMonth: Date;
  prevBillingMonth: Date;
  clientUsage: ClientBillingAgreement[] = [];
  ltbProducts: any[] = [];
  // #endregion

  // #region private
  private _existingManualEvent: any;
  private _billingAgreementSub: Subscription;
  private _manualUsageSub: Subscription;
  // #endregion

  constructor(
    private _titleService: TitleService,
    private _frmbuilder: FormBuilder,
    public sharedDataService: SharedDataService,
    private _ltbDataService: LtbDataService,
    private _clientBillingAgreementService: ClientBillingAgreementService,
    private _manualUsageEventService: ManualUsageEventService) {

    this._titleService.Subtitle = 'Usage Event Entry';
    this.now = new Date();
    this.currentBillingMonth = new Date(this.now);
    // FIX ME: Use a better date library to handle this. It breaks on the last day of the month.
    this.prevBillingMonth = new Date(new Date(this.now.setMonth(this.now.getMonth() - 1)));
  }

  async ngOnInit() {
    // populate client list
    this.showSpinner = true;
    await this.sharedDataService.PopulateAviClientIds();
    this.showSpinner = false;

    // intialize FormGroup
    this.intializeForm();
  }

  async onSubmit() {
    if (this._manualUsageSub) {
      this._manualUsageSub.unsubscribe();
    }
    this.showSpinner = true;

    try {
      const formData = ManualFormMapper.MapToManualUsageEvent(this.manualUsageEventForm) as any;
      if (this.existingManualEvent && this.existingManualEvent.manualUsageEventId) {
        let resp = await this._manualUsageEventService.UpdateManualUsageEvent( formData as ManualUsageEventService.UpdateManualUsageEventParams ).toPromise();        
        formData.manualUsageEventId = resp.manualUsageEventId;
        this.sharedDataService.updateManualUsage(formData.manualUsageEventId, formData);
      } else {
        let newManulUsageResp = await this._manualUsageEventService.CreateManualUsageEvent( formData as ManualUsageEventService.CreateManualUsageEventParams ).toPromise();
        formData.manualUsageEventId = newManulUsageResp.manualUsageEventId;
      }
    } catch (error) {
      this.sharedDataService.HandleError(error);
    } finally {
      this._ltbDataService.currenntSlideOutShown.hide();
      this.showSpinner = false;
    }
  }

  // --------------------------------------------------

  async onValidateAviclient(id: string): Promise<void> {
    if (!this.manualUsageEventForm.get('aviClientId').valid) {
      this.manualUsageEventForm.get('productDescription').disable({onlySelf: true});
    } else {
      // valid client
      if (id.length === 0) {
        return;
      }

      try {
        this.showSpinner = true;
        // Data Service Call to get ClientUsageProducts
        let params = {clientId: id, feeType: 'Manual'};
        this.clientUsage = await this._clientBillingAgreementService.GetAllClientBillingAgreements(params as ClientBillingAgreementService.GetAllClientBillingAgreementsParams)
          .toPromise();
        this.clientUsage = this.clientUsage.sort((a , b) =>  a.productDescription.localeCompare(b.productDescription));
        this.manualUsageEventForm.get('product').enable({onlySelf: true});
      } catch (err) {
        this.sharedDataService.HandleError('Error Retrieving Client Billing Agreements: ' + err);
      } finally {
        this.showSpinner = false;
      }
    }
  }

  // --------------------------------------------------

  private intializeForm(): void {
    this.clientUsage = []; // resets the client usage
    let formGroup: LTBManualFormModel = {
      aviClientId: ['', [Validators.required]],
      product: [{value: '', disabled: true}, [Validators.required]],
      description: '',
      value: ['', [Validators.required,Validators.pattern('^[-]?[0-9]{1,7}(?:\\.[0-9]{1,2})?$')]],
      month: this.currentBillingMonth,
      category: '',

      // hidden field for type
      usageType: {value: this.usageEventTypes.base, hidden: true},
      // hidden field for ManualUsageID
      manualUsageEventId: '',
      // hidden field for DataLineageId
      dataLineageId: '',

      siteGroup: this._frmbuilder.group({
        siteName: '',
        city: '',
        state: '',
        zip: '',
      } as ManualUsageEvent),

      ocGroup: this._frmbuilder.group({
        storeNumber: '',
        primaryService: '',
        address: '',
        utilityName: '',
        meterNumber: '',
        dateRequested: [new Date().toJSON()
          .substring(0,10), []],
        requester: '',
        accountNumber: ''
      } as OpenClose),

      lfGroup: this._frmbuilder.group({
        billConsolidatedOn: '',
        serviceDescription: '',
        consolidationID: '',
        consolidationInvoiceNumber: '',
        consolidationFunded: '',
        daysToBill: [0, [isNumberValidator]],
      } as LateFunding),

      lufaGroup: this._frmbuilder.group({
        accountNumber: ''
      } as LateUtilityFeeAdjustment)
    }
    this.manualUsageEventForm = this._frmbuilder.group(formGroup);
    this.manualUsageEventForm.controls['value'].setValue(100);
    // on aviClientId change
    this.manualUsageEventForm.get('aviClientId').valueChanges.subscribe(val =>
      {
          this.resetManualUsageEventForm();
          this.onValidateAviclient(val);
    });
    // onusageProduct change
    this.manualUsageEventForm.get('product').valueChanges.subscribe(val =>
    {
        // reset usageType formcontrols
      if(this.manualUsageEventForm.dirty)
      {
          this.resetFormGroupsAfterProductValchange();
      }

      if(val !== null && val !== undefined)
      {
        let usage = this.clientUsage.find(c=> c.productDescription == val);
        if(usage) this.manualUsageEventForm.get('category').setValue(usage.category);
        // if(productDescriptionValue.match('/Product.*?af99./gi')
        if(val.toLowerCase().includes('open') && val.toLowerCase().includes('close'))
        {
          this.manualUsageEventForm.get('usageType').setValue(this.usageEventTypes.openClose);
        }
        else if(val.toLowerCase().includes('late funding'))
        {
          this.manualUsageEventForm.get('usageType').setValue(this.usageEventTypes.lateFunding);
        }
        else if(val.toLowerCase().includes('late utility fee adjustment'))
        {
          this.manualUsageEventForm.get('usageType').setValue(this.usageEventTypes.lateUtilityFundingAdjustment);
        }
        else
        {
          this.manualUsageEventForm.get('usageType').setValue(this.usageEventTypes.base);
        }
      }
    });
  }

  // --------------------------------------------------

  private resetFormGroupsAfterProductValchange(): void {
    if( this.manualUsageEventForm.get('siteGroup').dirty)
    {
      this.manualUsageEventForm.get('siteGroup').setErrors(null);
      this.manualUsageEventForm.get('siteGroup').reset('');
    }

    if( this.manualUsageEventForm.get('lfGroup').dirty)
    {
      this.manualUsageEventForm.get('lfGroup').setErrors(null);
      this.manualUsageEventForm.get('lfGroup').reset('');
      this.manualUsageEventForm.get('lfGroup.daysToBill').reset(0);
    }

    if( this.manualUsageEventForm.get('ocGroup').dirty)
    {
      this.manualUsageEventForm.get('ocGroup').setErrors(null);
      this.manualUsageEventForm.get('ocGroup').reset('');
    }

    if( this.manualUsageEventForm.get('value').dirty)
    {
      this.manualUsageEventForm.get('value').setErrors(null);
      this.manualUsageEventForm.get('value').reset(null);
    }

    if( this.manualUsageEventForm.get('description').dirty)
    {
      this.manualUsageEventForm.get('description').setErrors(null);
      this.manualUsageEventForm.get('description').reset('');
    }

    if( this.manualUsageEventForm.get('lufaGroup').dirty)
    {
      this.manualUsageEventForm.get('lufaGroup').setErrors(null);
      this.manualUsageEventForm.get('lufaGroup').reset('');
    }
  }

  private resetManualUsageEventForm(): void {
    this.manualUsageEventForm.get('usageType').reset(this.usageEventTypes.base);
    this.manualUsageEventForm.get('product').setErrors(null);
    this.manualUsageEventForm.get('product').reset('');
    this.manualUsageEventForm.get('value').setErrors(null);
    this.manualUsageEventForm.get('value').reset('');
    this.manualUsageEventForm.get('category').setValue('');
    this.manualUsageEventForm.get('category').setErrors(null);
    this.manualUsageEventForm.get('month').setValue('');
    this.manualUsageEventForm.get('month').setErrors(null);

    // reset product change
    this.manualUsageEventForm.get('siteGroup').setErrors(null);
    this.manualUsageEventForm.get('siteGroup').reset('');
    this.manualUsageEventForm.get('lfGroup').setErrors(null);
    this.manualUsageEventForm.get('lfGroup').reset('');
    this.manualUsageEventForm.get('lfGroup.daysToBill').reset(0);
    this.manualUsageEventForm.get('ocGroup').setErrors(null);
    this.manualUsageEventForm.get('ocGroup').reset('');
    this.manualUsageEventForm.get('value').setErrors(null);
    this.manualUsageEventForm.get('value').reset(null);
    this.manualUsageEventForm.get('description').setErrors(null);
    this.manualUsageEventForm.get('description').reset('');
    this.manualUsageEventForm.get('lufaGroup').setErrors(null);
    this.manualUsageEventForm.get('lufaGroup').reset('');
  }

  // --------------------------------------------------
}
